import { useEffect, useRef } from 'react';

import { useAuth } from '@/contexts/auth.context';
import { useUserContext } from '@/contexts/user.context';

/**
 * Syncs the authenticated user from better-auth session into the UserContext.
 * When the user is already logged in (session cookie), fetches the user via /users/me
 * and populates UserContext.user for features that depend on it (e.g. vocal CV editing).
 */
export function useUserSync() {
  const { session, isLoading: isPending } = useAuth();
  const { user, loginWithExistingUser } = useUserContext();
  const isLoadingUserRef = useRef(false);

  useEffect(() => {
    (async () => {
      // Only sync if:
      // 1. Session is ready (not pending)
      // 2. User is authenticated (session exists)
      // 3. User is not already loaded in UserContext
      // 4. We're not already loading
      if (isPending || !session?.user || user?.id || isLoadingUserRef.current) {
        return;
      }

      isLoadingUserRef.current = true;

      try {
        await loginWithExistingUser();
      } catch (error) {
        console.error('Failed to sync user to UserContext:', error);
      } finally {
        isLoadingUserRef.current = false;
      }
    })();
  }, [session?.user, isPending, user?.id, loginWithExistingUser]);
}
